import type { LinkedBoard } from "./table.data.interface";
import type { LiveItemLocation } from "../../hooks/api/useLinkedBoardLocations";

/** A linked board after the persisted record has been reconciled with Monday. */
export interface ResolvedBoard {
  boardId: string;
  boardName?: string;
  groupName?: string;
  itemId: string;
  /** Only set by the SIMULATE_10_BOARDS preview scaffolding in BoardsHeader. */
  _sim?: boolean;
}

/**
 * Reconciles one persisted linkedBoard record against the live location Monday
 * reports for that item.
 *
 * Background: SIMB never receives an event when an item is moved to a different
 * board, so `board.boardId` / `board.boardName` can point at a board the item
 * left long ago. `live` is the authoritative answer when present, but it is
 * fetched asynchronously and can be undefined (still loading, request failed,
 * or the item is on a board this user cannot read).
 */
export const resolveLinkedBoard = (
  board: LinkedBoard,
  live: LiveItemLocation | undefined,
): ResolvedBoard => {
  // No live answer yet — still fetching, the request failed, or Monday returned
  // nothing for this item. Keep the persisted record so the chip stays
  // populated rather than flashing "Unknown Board" on first paint.
  if (!live) {
    return {
      boardId: board.boardId,
      boardName: board.boardName,
      groupName: board.group,
      itemId: board.itemId,
    };
  }

  const hasMoved = live.boardId !== board.boardId;

  return {
    boardId: live.boardId,
    // Once the item has moved, the persisted name and group describe the board
    // it left, so they are not safe fallbacks: pairing a live boardId with a
    // name from a different board would mislabel the chip and feed a
    // mismatched pair into the remove modal. Leave them undefined instead and
    // let BoardGroupChip show its "Private Board" / "Group" placeholders.
    boardName: hasMoved ? live.boardName : (live.boardName ?? board.boardName),
    groupName: hasMoved ? live.groupName : (live.groupName ?? board.group),
    itemId: board.itemId,
  };
};
